import React, { useCallback } from 'react';
import { Grid, TextField } from '@material-ui/core';
import { useStarAiContext } from 'star-ai';

import {
  ComponentController,
  InformationMessage
} from 'components/common';
import { GeneralDetailsFields, HidacEntity, TotalNumberOfPolicyReferencesName } from 'components/SubmissionWizard/Helpers';
import { createMajorClassStructure, S4000Entity } from 'components/SubmissionWizard/Helpers';
import TotalPoliciesField from 'components/SubmissionWizard/Fields/TotalPoliciesField';
import { AssuredAndPolicyReferenceControl } from './common';

import '../GeneralDetails.css';

const AccountsReceivableSection = ({
  fields,
  dropdownsData,
  control,
  errors,
  forceClearDatasourceState,
  setFormState,
  setValidationValue,
  setLoading,
  setBannerState,
  setForceClearDatasourceState,
  resetFieldValidationState,
  modellingMajorClasses
}) => {

  // star ai
  const ai = useStarAiContext();
 
  const maxCommentsLength = 1000;

  const onAssuredPolicyChanged = useCallback(assured => {
    let majorClass = createMajorClassStructure(assured, modellingMajorClasses);

    setFormState(previous => ({
      ...previous,
      fields: {
        ...previous.fields,
        majorClass: majorClass,
        requiredModellingPriority: majorClass?.isModellingPriority
          ? previous.dropdownsData.modellingPriorities[0]
          : ''
      }
    }));

    resetFieldValidationState("secondAssured");
    setForceClearDatasourceState({ "secondAssured": true });
  },[setFormState, resetFieldValidationState, setForceClearDatasourceState]);

  
  const getRequiredError = name => {
    return errors[name]?.type === 'required' &&
      <span role="alert">"{GeneralDetailsFields[name]}" is required</span>;
  }

  const onFieldChange = useCallback((name, value) => {
    setFormState(previous => ({
      ...previous,
      fields: { ...previous.fields, [name]: value ?? '' }
    }));
  }, [setFormState]);

  const renderLookupForAssuredAndPolicyReference = (lookupType = "lookupForAssuredAndPolicyReference") => {
    return (
      <AssuredAndPolicyReferenceControl
        lookupType={lookupType}
        errors={errors}
        control={control}
        fields={fields}
        onChange={value => onAssuredPolicyChanged(value)}
        additionalRequestData={{ businessEntities: dropdownsData.entitiesForFilter }}
        setFormState={setFormState}
        setValidationValue={setValidationValue}
        setLoading={setLoading}
        setBannerState={setBannerState}
        forceClearDatasourceState={forceClearDatasourceState}
        forceEnabled={true}
        includePlacingBasis={true}
        inputRef={ai?.fieldsRef?.[lookupType]}
      />
    );
  }

  const renderAssured = (assuredType = "assured") => {
    let name = "assured";

    return (
      <Grid item>
        <TextField
          id={name}
          name={name}
          fullWidth
          disabled
          label={GeneralDetailsFields[assuredType]}
          value={fields.assured?.assured ?? fields.reassured ?? ""}
          inputRef={ai?.fieldsRef?.[name]}
        />
      </Grid>
    );
  }

  const renderPolicyReferenceNumber = (policyReferenceNumberType = "policyReferenceNumber") => {
    let name = "policyReferenceNumber";

    return (
      <Grid item>
        <TextField
          id={name}
          name={name}
          fullWidth
          disabled
          label={GeneralDetailsFields[policyReferenceNumberType]}
          value={fields.policyReferenceNumber}
          inputRef={ai?.fieldsRef?.[name]}
        />
      </Grid>
    );
  }

  const renderTotalNumberOfReferences = () => {
    return (
      <Grid item>
        <TotalPoliciesField
          name={TotalNumberOfPolicyReferencesName}
          label={GeneralDetailsFields[TotalNumberOfPolicyReferencesName]}
          required={true}
          control={control}
          value={fields[TotalNumberOfPolicyReferencesName]}
          errors={errors}
          setFormState={setFormState}
          setValidationValue={setValidationValue}
          getRequiredError={getRequiredError}
          inputRef={ai?.fieldsRef?.[TotalNumberOfPolicyReferencesName]}
        />
      </Grid>
    );
  }

  const renderComments = () => {
    return (
      <Grid item>
        <ComponentController
          name="commentsBordereaux"
          control={control}
          rules={{ maxLength: maxCommentsLength }}
          render={({ field: { name, onBlur, onChange } }) =>
            <TextField
              id={name}
              name={name}
              label={GeneralDetailsFields[name]}
              fullWidth
              onBlur={onBlur}
              onChange={e => { onFieldChange(name, e.target?.value); onChange(e); }}
              value={fields.commentsBordereaux}
              error={!!errors[name]}
              helperText={<InformationMessage
                fieldName={GeneralDetailsFields.commentsBordereaux}
                fieldErrors={errors.commentsBordereaux}
                maxLength={maxCommentsLength}
                showLengthInfo={true}
                length={fields.commentsBordereaux.length}
              />}
              inputRef={ai?.fieldsRef?.[name]}
            />
          }
        />
      </Grid>
    );
  }

  return <>
    {renderLookupForAssuredAndPolicyReference()}
    {renderPolicyReferenceNumber()}
    {renderAssured()}
    {renderTotalNumberOfReferences()}
    {renderComments()}
  </>;
}

export default AccountsReceivableSection;